import { Box, Text, TextProps, ThemeContext } from 'grommet';
import { ColorType } from 'grommet/utils';
import { Project, TalentManagerEngagement, TProjectId } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProjectStore, useUserStore } from 'src/context';
import { pathForProjectSwitch } from 'src/utils/projectPath';
import { Selector, TalonTip } from '../ui';
import { FavoriteFilled } from '../ui/icons/FilledIcons';

export type ProjectSelectorProps = {
  textColor?: ColorType;
  /** A talent manager's active engagements: only their projects are listed, grouped by agency */
  engagements?: TalentManagerEngagement[];
};

// Long names end in an ellipsis in the bar, with the whole name in the tooltip
const nameProps: TextProps & { style: React.CSSProperties } = {
  size: '1rem',
  truncate: true,
  style: { maxWidth: '200px', display: 'block' },
};

export const ProjectSelector = observer(({ textColor = 'accent-1', engagements }: ProjectSelectorProps) => {
  /** Context **/
  const navigate = useNavigate();
  const location = useLocation();
  const projectStore = useProjectStore();
  const userStore = useUserStore();

  /** State **/
  const [projectList, setProjectList] = useState(projectStore.projectList);
  const [favorites, setFavorites] = useState<string[]>([]);

  /** Methods **/

  // Only the user's choice here navigates, and only to keep a project page's URL naming the
  // project shown (ProjectRoute selects the project a URL names). This used to run whenever the
  // current project changed for any reason, sending the user to the project's home page each time
  const selectProject = (projectId: TProjectId) => {
    if (projectId === projectStore.currentProject?.id) return;
    const project = projectStore.setCurrentProjectId(projectId);
    const path = project && pathForProjectSwitch(location.pathname, project.name);
    if (path) navigate(path);
  };

  /** Effects **/

  useEffect(() => {
    const getProjects = async () => {
      await projectStore.fetchProjectList();
    };
    getProjects();
  }, [projectStore]);

  useEffect(() => {
    const favs = projectStore.projectList?.filter((p) => userStore.user?.favorite_project_ids?.includes(p.id));
    favs?.sort((a, b) => a.name.localeCompare(b.name));
    const notFavs = projectStore.projectList?.filter((p) => !userStore.user?.favorite_project_ids?.includes(p.id));
    notFavs?.sort((a, b) => a.name.localeCompare(b.name));
    setProjectList([...(favs || []), ...(notFavs || [])]);
  }, [projectStore.projectList, userStore.user?.favorite_project_ids]);

  // A talent manager's projects, by agency, then name
  const engagementFor = (p: Project) => engagements?.find((e) => e.project_id === p.id);
  const agencyOf = (p: Project) => engagementFor(p)?.group?.name ?? '';
  const options = (projectList ?? []).filter((p) => !engagements || engagementFor(p));
  if (engagements) options.sort((a, b) => agencyOf(a).localeCompare(agencyOf(b)) || a.name.localeCompare(b.name));
  // In the list, the first project of each agency carries the agency's name above it
  const agencyHeading = (p: Project) => {
    if (!engagements) return null;
    const i = options.findIndex((o) => o.id === p.id);
    if (i < 0 || (i > 0 && agencyOf(options[i - 1]) === agencyOf(p))) return null;
    return (
      <Text size="12px" color="text-label">
        {agencyOf(p)}
      </Text>
    );
  };
  const viewOnly = (p: Project) =>
    engagementFor(p)?.can_edit_project === false && (
      <Text size="12px" weight={600} color="pill-warning-text" margin={{ left: 'small' }}>
        View only
      </Text>
    );

  useEffect(() => {
    setFavorites(userStore.user?.favorite_project_ids || []);
  }, [userStore.user?.favorite_project_ids]);

  /** Render **/
  return (
    <>
      {projectStore.currentProject?.id && (
        <TalonTip content={<Text>{projectStore.currentProject.name}</Text>}>
          <Box alignSelf="center" flex={false} round="8px" style={{ background: 'rgba(255, 255, 255, 0.14)' }}>
            {/* The current project's row is tinted, as in the header's other menus */}
            <ThemeContext.Extend value={{ global: { colors: { selected: 'brand-tint' } } }}>
              <Selector
                options={options}
                value={projectStore.currentProject.id}
                onChange={(val) => selectProject(val)}
                // In the bar: the name (and favorite star) on the blue; agency and access are beside it
                valueLabel={
                  <Box direction="row" pad="small" align="center">
                    {favorites.includes(projectStore.currentProject.id) && (
                      <FavoriteFilled $filled="true" size="small" />
                    )}
                    <Text {...nameProps} color={textColor}>
                      {favorites.includes(projectStore.currentProject.id) && <>&nbsp; </>}
                      {projectStore.currentProject.name}
                    </Text>
                  </Box>
                }
                // The list is light, like the header's other menus
                dropProps={{ background: 'white', round: '8px', elevation: 'medium' }}
                labelKey={(p: Project) => (
                  <Box pad="small">
                    {agencyHeading(p)}
                    <Box direction="row" align="center">
                      {favorites.includes(p.id) && <FavoriteFilled $filled="true" size="small" />}
                      <Text
                        {...nameProps}
                        weight={favorites.includes(p.id) ? 500 : undefined}
                        color={p.id === projectStore.currentProject?.id ? 'brand' : 'text-heading'}
                      >
                        {favorites.includes(p.id) && <>&nbsp; </>}
                        {p.name}
                      </Text>
                      {viewOnly(p)}
                    </Box>
                  </Box>
                )}
              />
            </ThemeContext.Extend>
          </Box>
        </TalonTip>
      )}
    </>
  );
});
